import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchPricing, type PricingResponse } from './pricing-client';

export interface PricingState {
  pricing: PricingResponse | null;
  loading: boolean;
  /** Re-fetch. Passed to each card's Retry button. */
  reload: () => void;
}

/**
 * One `/api/pricing` fetch for the whole page.
 *
 * WHY IT IS A HOOK AND NOT TWO FETCHES. The calculator and the rate card are
 * two cards but one dataset — the same rates, the same conversions, the same
 * shapes. Letting each card fetch its own (which is what `PricingCard` did
 * when it was the only consumer) would put two requests on every page load
 * and, worse, allow the two cards to disagree: the endpoint is versioned by
 * deploy, and a retry after a deploy would leave one card pricing against the
 * old table while the other priced against the new one, on screen, at the same
 * time. `Billing` calls this once and hands both cards the same object.
 *
 * The cards keep their own Retry buttons all the same. `reload` refetches for
 * both, which is the correct blast radius: there is one failure here, not two.
 */
export function usePricing(): PricingState {
  const [pricing, setPricing] = useState<PricingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // Guards React 18 StrictMode's deliberate double-invoke of effects in
  // development, the same way `Billing`'s own wallet fetch does.
  const started = useRef(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setPricing(await fetchPricing());
    setLoading(false);
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    reload();
  }, [reload]);

  return { pricing, loading, reload };
}
